// src/services/notificationService.js
import supabase from '../supabase';

/**
 * Obtiene las notificaciones del usuario actual con opciones avanzadas
 * @param {Object} options - Opciones de filtrado
 * @param {number} options.limit - Límite de notificaciones a obtener
 * @param {number} options.offset - Índice desde el cual empezar
 * @param {boolean} options.includeRead - Incluir notificaciones leídas
 * @param {string} options.orderBy - Campo por el cual ordenar
 * @param {string} options.type - Filtrar por tipo específico
 * @returns {Promise<{notifications: Array, count: number, hasMore: boolean}>} Notificaciones y metadatos
 */
export const getUserNotifications = async (options = {}) => {
  const {
    limit = 20,
    offset = 0,
    includeRead = true,
    orderBy = 'created_at',
    type = null
  } = options;

  try {
    // Obtener el usuario actual
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) throw new Error('No hay usuario autenticado');

    // Construir la consulta
    let query = supabase
      .from('notifications')
      .select('*', { count: 'exact' })
      .eq('user_id', user.id)
      .order(orderBy, { ascending: false });

    // Aplicar filtros adicionales
    if (!includeRead) {
      query = query.eq('is_read', false);
    }
    
    if (type) {
      query = query.eq('notification_type', type);
    }
    
    // Aplicar paginación
    if (limit > 0) {
      query = query.range(offset, offset + limit - 1);
    }

    // Obtener las notificaciones
    const { data: notifications, error, count } = await query;

    if (error) throw error;
    
    // Calcular si hay más resultados
    const hasMore = count > (offset + limit);

    return { 
      notifications: notifications || [], 
      count, 
      hasMore 
    };
  } catch (error) {
    console.error('Error obteniendo notificaciones:', error);
    throw error;
  }
};

/**
 * Obtiene el conteo de notificaciones no leídas
 * @returns {Promise<number>} Conteo de notificaciones no leídas
 */
export const getUnreadNotificationsCount = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) return 0;

    const { count, error } = await supabase
      .from('notifications')
      .select('id', { count: 'exact' })
      .eq('user_id', user.id)
      .eq('is_read', false);

    if (error) throw error;

    return count || 0;
  } catch (error) {
    console.error('Error obteniendo conteo de notificaciones:', error);
    return 0; // Retornamos 0 en caso de error para evitar bloqueos en UI
  }
};

/**
 * Marca una notificación como leída
 * @param {string} notificationId - ID de la notificación
 * @returns {Promise<boolean>} Éxito de la operación
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ 
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('id', notificationId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error marcando notificación como leída:', error);
    throw error;
  }
};

/**
 * Marca todas las notificaciones del usuario como leídas
 * @param {Object} options - Opciones de filtrado
 * @param {string} options.type - Tipo específico a marcar como leído (opcional)
 * @returns {Promise<boolean>} Éxito de la operación
 */
export const markAllNotificationsAsRead = async (options = {}) => {
  const { type = null } = options;
  
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) throw new Error('No hay usuario autenticado');

    // Construir actualización
    let query = supabase
      .from('notifications')
      .update({ 
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('user_id', user.id)
      .eq('is_read', false);
    
    // Filtrar por tipo si es necesario
    if (type) {
      query = query.eq('notification_type', type);
    }

    const { error } = await query;

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error marcando todas las notificaciones como leídas:', error);
    throw error;
  }
};

/**
 * Elimina una notificación
 * @param {string} notificationId - ID de la notificación
 * @returns {Promise<boolean>} Éxito de la operación
 */
export const deleteNotification = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error eliminando notificación:', error);
    throw error;
  }
};

/**
 * Elimina todas las notificaciones de un usuario con filtrado opcional
 * @param {Object} options - Opciones de filtrado
 * @param {string} options.type - Tipo específico a eliminar (opcional)
 * @param {boolean} options.readOnly - Solo eliminar leídas (opcional)
 * @returns {Promise<{success: boolean, count: number}>} Resultado y conteo
 */
export const deleteAllNotifications = async (options = {}) => {
  const { 
    type = null,
    readOnly = false
  } = options;
  
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) throw new Error('No hay usuario autenticado');

    // Construir eliminación
    let query = supabase
      .from('notifications')
      .delete({ count: 'exact' })
      .eq('user_id', user.id);
    
    // Aplicar filtros adicionales
    if (type) {
      query = query.eq('notification_type', type);
    }
    
    if (readOnly) {
      query = query.eq('is_read', true);
    }

    const { error, count } = await query;

    if (error) throw error;

    return { 
      success: true, 
      count: count || 0 
    };
  } catch (error) {
    console.error('Error eliminando notificaciones:', error);
    return { 
      success: false, 
      error: error.message,
      count: 0
    };
  }
};

/**
 * Elimina notificaciones antiguas (mayores a 60 días)
 * @returns {Promise<{success: boolean, count: number}>} Resultado y conteo
 */
export const cleanupOldNotifications = async () => {
  try {
    // Calcular fecha límite (hace 60 días)
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 60);
    
    // Ejecutar eliminación
    const { error, count } = await supabase
      .from('notifications')
      .delete({ count: 'exact' })
      .lt('created_at', cutoffDate.toISOString());
    
    if (error) throw error;
    
    return {
      success: true,
      count: count || 0
    };
  } catch (error) {
    console.error('Error durante limpieza de notificaciones:', error);
    return {
      success: false,
      error: error.message,
      count: 0
    };
  }
};

/**
 * Suscribe a nuevas notificaciones en tiempo real
 * @param {Function} callback - Función a llamar cuando llega una nueva notificación
 * @returns {Promise<Object>} Objeto con la suscripción
 */
export const subscribeToNotifications = async (callback) => {
  try {
    // Obtener el usuario de forma asíncrona
    const { data, error } = await supabase.auth.getUser();
    
    if (error) {
      console.error('Error obteniendo usuario para suscripción:', error);
      return { subscription: null };
    }
    
    const user = data?.user;
    
    if (!user || !user.id) {
      console.warn('No hay usuario autenticado para suscribir a notificaciones');
      return { subscription: null };
    }
    
    console.log('Suscribiendo a notificaciones para usuario:', user.id);
    
    // Crear canal de suscripción
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT', 
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        }, 
        payload => {
          if (callback && typeof callback === 'function') {
            callback(payload.new);
          }
        }
      )
      .subscribe();
    
    return { subscription: channel };
  } catch (error) {
    console.error('Error suscribiéndose a notificaciones:', error);
    return { subscription: null };
  }
};

/**
 * Crea una notificación para un usuario específico
 * @param {string} userId - ID del usuario
 * @param {Object} data - Datos de la notificación
 * @returns {Promise<Object|null>} - Notificación creada o null
 */
export const createNotification = async (userId, data) => {
  try {
    // Verificar datos requeridos
    if (!userId) throw new Error('Se requiere ID de usuario');
    if (!data.title) throw new Error('Se requiere título de notificación');
    if (!data.message) throw new Error('Se requiere mensaje de notificación');
    
    // Preparar datos
    const notification = {
      user_id: userId,
      title: data.title,
      message: data.message,
      notification_type: data.notification_type || 'system',
      related_entity_type: data.related_entity_type || null,
      related_entity_id: data.related_entity_id || null,
      is_read: false
    };
    
    // Insert directo sin intentar RPC
    const { data: insertedNotification, error } = await supabase
      .from('notifications')
      .insert(notification)
      .select()
      .single();
    
    if (error) throw error;
    
    return insertedNotification;
  } catch (error) {
    console.error('Error creando notificación:', error);
    throw error;
  }
};

/**
 * Crea notificaciones para múltiples usuarios
 * @param {string[]} userIds - Array de IDs de usuarios
 * @param {Object} data - Datos comunes para todas las notificaciones
 * @returns {Promise<Object[]>} - Notificaciones creadas
 */
export const createBulkNotifications = async (userIds, data) => {
  try {
    if (!userIds || !userIds.length) {
      throw new Error('Se requiere al menos un ID de usuario');
    }
    
    if (!data.title || !data.message) {
      throw new Error('Se requieren título y mensaje');
    }

    // Preparar notificaciones
    const notifications = userIds.map(userId => ({
      user_id: userId,
      title: data.title,
      message: data.message,
      notification_type: data.notification_type || 'system',
      related_entity_type: data.related_entity_type || null,
      related_entity_id: data.related_entity_id || null,
      is_read: false
    }));
    
    // Insertar en lotes de 100 para evitar problemas
    const batchSize = 100;
    const results = [];
    
    for (let i = 0; i < notifications.length; i += batchSize) {
      const batch = notifications.slice(i, i + batchSize);
      
      // Limitar frecuencia para evitar sobrecarga
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Esperar 500ms entre lotes
      }
      
      const { data: created, error } = await supabase
        .from('notifications')
        .insert(batch)
        .select();
      
      if (error) {
        console.error(`Error en lote ${i}-${i + batch.length}:`, error);
        continue; // Continuar con el siguiente lote en caso de error
      }
      
      if (created) {
        results.push(...created);
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error creando notificaciones en masa:', error);
    throw error;
  }
};

/**
 * Obtiene preferencias de notificaciones del usuario
 * @returns {Promise<Object>} Preferencias del usuario
 */
export const getUserNotificationPreferences = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) throw new Error('No hay usuario autenticado');
    
    // Obtener perfil de usuario
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('preferences')
      .eq('id', user.id)
      .single();
    
    if (profileError) throw profileError;
    
    // Extraer preferencias o usar valores predeterminados
    const preferences = profile?.preferences || {};
    
    // Asegurar que exista la estructura de notificaciones
    if (!preferences.notifications) {
      preferences.notifications = {
        app: true,
        email: true,
        events: true,
        jobOffers: true,
        newsletter: true
      };
    }
    
    return preferences.notifications;
  } catch (error) {
    console.error('Error obteniendo preferencias de notificaciones:', error);
    // Retornar valores predeterminados en caso de error
    return {
      app: true,
      email: true,
      events: true,
      jobOffers: true,
      newsletter: true
    };
  }
};

/**
 * Actualiza preferencias de notificaciones
 * @param {Object} preferences - Nuevas preferencias
 * @returns {Promise<Object>} Preferencias actualizadas
 */
export const updateNotificationPreferences = async (preferences) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) throw new Error('No hay usuario autenticado');
    
    // Obtener perfil actual
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('preferences')
      .eq('id', user.id)
      .single();
    
    if (profileError) throw profileError;
    
    // Actualizar preferencias manteniendo la estructura existente
    const currentPreferences = profile?.preferences || {};
    
    const updatedPreferences = {
      ...currentPreferences,
      notifications: {
        ...(currentPreferences.notifications || {}),
        ...preferences
      }
    };
    
    // Guardar preferencias actualizadas
    const { data: updatedProfile, error: updateError } = await supabase
      .from('user_profiles')
      .update({ preferences: updatedPreferences })
      .eq('id', user.id)
      .select('preferences')
      .single();
    
    if (updateError) throw updateError;
    
    return updatedProfile?.preferences?.notifications || preferences;
  } catch (error) {
    console.error('Error actualizando preferencias de notificaciones:', error);
    throw error;
  }
};